import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  CdsAppEvent,
  CdsAppEventAction,
  CustomerBalancePaymentCancelPayload,
  CustomerBalancePaymentRequestPayload,
  CustomerBalancePaymentResultPayload,
  buildCdsAppEventResultSubject,
  buildCdsAppEventSubject,
} from '@oolio-group/domain';
import { isTap2PayOutcomeUnknown } from '@oolio-group/client-utils';
import { tap2PayEmbeddedManager } from '../../utils/Tap2PayEmbeddedManager';
import { handledPaymentRequests } from '../../utils/handledPaymentRequests';
import { Session } from '../../state/Session';
import Sentry from '../../utils/Sentry/Sentry';
import {
  BaseEventEmitter,
  isLocalCommsEligible as canUseLocalComms,
  localComms,
  subscribeAppEventsStream,
} from '../../utils/localComms';
import { localCommsSubject } from '../../state/localComms';
import { cdsAppEventsSubject } from './cdsAppEventsObservable';
import useBehaviorSubjectState from '../rxjs/useSubjectState';

export type BalancePaymentStatus = 'idle' | 'processing' | 'complete' | 'error';

export interface BalancePaymentState {
  status: BalancePaymentStatus;
  amount: number;
}

const IDLE_STATE: BalancePaymentState = { status: 'idle', amount: 0 };

export const useBalancePaymentRequest = (session?: Partial<Session>) => {
  const { value: isLocalCommsEligible } =
    useBehaviorSubjectState(localCommsSubject);
  const [state, setState] = useState<BalancePaymentState>(IDLE_STATE);
  const processingRef = useRef(false);
  const chargingRequestIdRef = useRef<string | null>(null);
  const displayedRequestIdRef = useRef<string | null>(null);
  const abortedRequestIdRef = useRef<string | null>(null);
  const sessionRef = useRef(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const reset = useCallback(() => setState(IDLE_STATE), []);

  // Without this POS keeps polling the gateway for a charge that was never
  // made, and its processing screen stays up until the poll times out.
  const pushFailedResultToPos = useCallback(
    async (payload: CustomerBalancePaymentRequestPayload, status?: string) => {
      const currentSession = sessionRef.current;
      const storeId = currentSession?.currentStore?.id;
      const deviceId = currentSession?.device?.id;
      if (!canUseLocalComms() || !storeId || !deviceId) return;

      const result: CustomerBalancePaymentResultPayload = {
        paymentRequestId: payload.paymentRequestId,
        merchantReference: payload.merchantReference,
        status,
      };
      const event: CdsAppEvent = {
        id: uuidv4(),
        action: CdsAppEventAction.CUSTOMER_BALANCE_PAYMENT_RESULT,
        organizationId: currentSession?.currentOrganization?.id ?? '',
        venueId: currentSession?.currentStore?.venue?.id,
        storeId,
        deviceId,
        timestamp: Date.now(),
        payload: JSON.stringify(result),
      };

      const error = await localComms.pushAppEventToStream(
        buildCdsAppEventResultSubject(storeId, deviceId),
        event,
      );
      if (error) {
        Sentry.captureMessage('Balance payment result could not reach POS', {
          level: 'warning',
          extra: {
            merchantReference: payload.merchantReference,
            errorMessage: error.message,
          },
        });
      }
    },
    [],
  );

  const runPayment = useCallback(
    async (payload: CustomerBalancePaymentRequestPayload) => {
      // POS finds this charge on the gateway by merchantReference alone — it is
      // never recorded on an order. Charging under any other reference orphans
      // the money, so refuse rather than let the SDK generate its own.
      if (!payload.merchantReference) {
        Sentry.captureMessage(
          'Balance payment request arrived without a merchant reference',
          { level: 'error', extra: { payload } },
        );
        setState({ status: 'error', amount: payload.amount ?? 0 });
        return;
      }

      // Returning quietly here would leave the customer looking at an idle
      // screen while POS polls the gateway for a charge that never starts.
      if (processingRef.current || tap2PayEmbeddedManager.isPaymentInProgress) {
        Sentry.captureMessage(
          'Balance payment request arrived while another payment was running',
          {
            level: 'warning',
            extra: { merchantReference: payload.merchantReference },
          },
        );
        setState({ status: 'error', amount: payload.amount ?? 0 });
        return;
      }

      processingRef.current = true;
      chargingRequestIdRef.current = payload.paymentRequestId;
      displayedRequestIdRef.current = payload.paymentRequestId;
      setState({ status: 'processing', amount: payload.amount });

      try {
        const result = await tap2PayEmbeddedManager.pay({
          amount: payload.amount.toFixed(2),
          currency: payload.currency,
          merchantReference: payload.merchantReference,
        });

        const abortedByPos =
          abortedRequestIdRef.current === payload.paymentRequestId;
        if (result.success) {
          setState({ status: 'complete', amount: payload.amount });
        } else if (abortedByPos) {
          setState(IDLE_STATE);
        } else {
          setState({ status: 'error', amount: payload.amount });
        }

        if (
          !result.success &&
          !abortedByPos &&
          !isTap2PayOutcomeUnknown(result)
        ) {
          pushFailedResultToPos(payload, result.status).catch(error =>
            Sentry.captureException(error, {
              extra: {
                merchantReference: payload.merchantReference,
                context: 'cds-balance-result-push',
              },
            }),
          );
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            merchantReference: payload.merchantReference,
            customerId: payload.customerId,
            context: 'cds-balance-payment',
          },
        });
        setState({ status: 'error', amount: payload.amount });
      } finally {
        processingRef.current = false;
        chargingRequestIdRef.current = null;
        if (abortedRequestIdRef.current === payload.paymentRequestId) {
          abortedRequestIdRef.current = null;
        }
        if (displayedRequestIdRef.current === payload.paymentRequestId) {
          displayedRequestIdRef.current = null;
        }
      }
    },
    [pushFailedResultToPos],
  );

  const handleEvents = useCallback(
    (events: CdsAppEvent[]) => {
      events.forEach(event => {
        const isRequest =
          event.action === CdsAppEventAction.CUSTOMER_BALANCE_PAYMENT_REQUEST;
        const isCancel =
          event.action === CdsAppEventAction.CUSTOMER_BALANCE_PAYMENT_CANCEL;
        if (!isRequest && !isCancel) return;

        // Guards a redelivery on resubscribe.
        if (handledPaymentRequests.has(event.id)) return;
        handledPaymentRequests.add(event.id);

        try {
          if (isCancel) {
            const cancel = JSON.parse(
              event.payload ?? '{}',
            ) as CustomerBalancePaymentCancelPayload;
            // A finished request is no longer displayed and must keep
            // showing its result.
            if (displayedRequestIdRef.current !== cancel.paymentRequestId) {
              return;
            }
            // The card reader stays open until it is aborted. POS checks the
            // gateway after cancelling, so a tap that beat the abort is still
            // recorded.
            if (chargingRequestIdRef.current === cancel.paymentRequestId) {
              abortedRequestIdRef.current = cancel.paymentRequestId;
              tap2PayEmbeddedManager.abortTransaction();
              return;
            }
            displayedRequestIdRef.current = null;
            setState(IDLE_STATE);
            return;
          }

          const payload = JSON.parse(
            event.payload ?? '{}',
          ) as CustomerBalancePaymentRequestPayload;
          runPayment(payload);
        } catch (error) {
          Sentry.captureException(error, {
            extra: { eventId: event.id, context: 'cds-app-event-payload' },
          });
        }
      });
    },
    [runPayment],
  );

  // Declared before the sources so it is listening when they start.
  useEffect(() => {
    const subscription = cdsAppEventsSubject.subscribe(handleEvents);
    return () => subscription.unsubscribe();
  }, [handleEvents]);

  // Published as-is, so it can arrive as a JSON string, one event, or a batch.
  useEffect(() => {
    const storeId = session?.currentStore?.id;
    const deviceId = session?.device?.id;
    if (!isLocalCommsEligible || !storeId || !deviceId) return;

    let emitter: InstanceType<typeof BaseEventEmitter> | null = null;
    let cancelled = false;

    const subscribe = async () => {
      try {
        emitter = await subscribeAppEventsStream(
          buildCdsAppEventSubject(storeId, deviceId),
        );
        if (cancelled) {
          emitter?.remove();
          emitter = null;
          return;
        }
        emitter?.addListener((data: unknown) => {
          try {
            const raw: unknown =
              typeof data === 'string' ? JSON.parse(data) : data;
            // The native bridge hands the payload over as { events }, the same
            // wrapper the order stream unwraps.
            const wrapper = raw as { events?: string } | undefined;
            const parsed: unknown = wrapper?.events
              ? JSON.parse(wrapper.events)
              : raw;
            if (!parsed) return;
            cdsAppEventsSubject.next(
              (Array.isArray(parsed) ? parsed : [parsed]) as CdsAppEvent[],
            );
          } catch (error) {
            Sentry.captureException(error, {
              extra: { context: 'cds-app-events-localcomms-payload' },
            });
          }
        });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'cds-app-events-localcomms-subscribe' },
        });
      }
    };

    subscribe();

    return () => {
      cancelled = true;
      emitter?.remove();
      emitter = null;
    };
  }, [isLocalCommsEligible, session?.currentStore?.id, session?.device?.id]);

  return { balancePayment: state, resetBalancePayment: reset };
};
